import {
	ChangeDetectionStrategy,
	Component,
	OnDestroy,
	OnInit
} from '@angular/core';
import {filter, map, startWith, takeUntil} from 'rxjs/operators';
import {fromEvent, interval, merge, Subject} from 'rxjs';
import * as moment from 'moment';

import {AuthFacade} from 'src/app/store/auth-state/+state/auth.facade';
import {DigitalBoardFacade} from 'src/app/store/digital-board/+state/digital-board.facade';
import Order from 'src/app/models/Order';
import {OrderPool} from 'src/app/models/OrderPool';

type BoardItem = Order | OrderPool;

const POLL_INTERVAL_MS = 30_000;

@Component({
	selector: 'app-digital-order-board',
	templateUrl: './digital-order-board.page.html',
	styleUrls: ['./digital-order-board.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DigitalOrderBoardPage implements OnInit, OnDestroy {
	public readonly loading$ = this.digitalBoardFacade.loading$;
	public readonly error$ = this.digitalBoardFacade.error$;
	public readonly inPreparationOrders$ =
		this.digitalBoardFacade.inPreparationOrders$;
	public readonly readyOrders$ = this.digitalBoardFacade.readyOrders$;

	public readonly reconnecting$ = merge(
		fromEvent(window, 'online').pipe(map(() => false)),
		fromEvent(window, 'offline').pipe(map(() => true))
	).pipe(startWith(!navigator.onLine));

	private venueId: string | null = null;
	private wakeLock: any = null;
	private readonly onDestroy$ = new Subject<void>();

	constructor(
		private authFacade: AuthFacade,
		private digitalBoardFacade: DigitalBoardFacade
	) {}

	ngOnInit(): void {
		this.authFacade.userData$
			.pipe(
				filter(userData => !!userData),
				takeUntil(this.onDestroy$)
			)
			.subscribe(userData => {
				const venueId = userData?.venues?.[0];
				if (venueId && venueId !== this.venueId) {
					this.venueId = venueId;
					this.digitalBoardFacade.loadBoardData(venueId);
				}
			});

		interval(POLL_INTERVAL_MS)
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(() => {
				if (this.venueId && navigator.onLine) {
					this.digitalBoardFacade.loadBoardData(this.venueId);
				}
			});

		this.requestWakeLock();
		document.addEventListener('visibilitychange', this.onVisibilityChange);
	}

	ngOnDestroy(): void {
		this.onDestroy$.next();
		this.onDestroy$.complete();
		this.releaseWakeLock();
		document.removeEventListener('visibilitychange', this.onVisibilityChange);
	}

	trackByOrderId(_index: number, item: BoardItem): string {
		return item._id || this.getOrderNumber(item);
	}

	isOrderPool(item: BoardItem): item is OrderPool {
		return (item as OrderPool).orders !== undefined;
	}

	getOrderNumber(item: BoardItem): string {
		if (this.isOrderPool(item)) {
			return item.orders?.[0]?.code || '—';
		}
		return (item as Order).code || '—';
	}

	getOrderAriaLabel(item: BoardItem): string {
		const number = this.getOrderNumber(item);
		const asap = this.isAsapOrder(item);
		const time = this.getOrderTimeDisplay(item);
		const type = this.isOrderPool(item) ? 'Pooled order' : 'Order';
		let label = `${type} ${number}`;
		if (asap) {
			label += ', ASAP';
		} else if (time) {
			label += `, ${time}`;
		}
		return label;
	}

	private isAsapOrder(item: BoardItem): boolean {
		if (this.isOrderPool(item)) {
			return item.orders?.[0]?.asap || false;
		}
		return (item as Order).asap || false;
	}

	private getOrderTimeDisplay(item: BoardItem): string {
		if (this.isOrderPool(item)) {
			const first = item.orders?.[0];
			if (first?.asap) return 'ASAP';
			if (item.scheduledDeliveryAt) {
				return moment(item.scheduledDeliveryAt).format('HH:mm');
			}
			if (first?.orderAt) return moment(first.orderAt).format('HH:mm');
			return '';
		}
		const order = item as Order;
		if (order.asap) return 'ASAP';
		if (order.orderAt) return moment(order.orderAt).format('HH:mm');
		return '';
	}

	private onVisibilityChange = (): void => {
		if (document.visibilityState === 'visible') {
			this.requestWakeLock();
			if (this.venueId) {
				this.digitalBoardFacade.loadBoardData(this.venueId);
			}
		}
	};

	private async requestWakeLock(): Promise<void> {
		try {
			const anyNavigator: any = navigator;
			if (anyNavigator.wakeLock?.request) {
				this.wakeLock = await anyNavigator.wakeLock.request('screen');
			}
		} catch {
			// Wake Lock API unsupported or denied — silently ignore.
		}
	}

	private releaseWakeLock(): void {
		try {
			this.wakeLock?.release?.();
		} catch {
			// ignore
		}
		this.wakeLock = null;
	}
}
